import { IDirectionDepricated } from "../types/types";
import { generateRandomString, getSqaureAroundCoordinate, PartialMcBimSpace } from "../utils";
import { IDevice, IMcBimItem, IObjectId, IRoom, IRoomSummary } from "@backend/src/schemas";

export type ITile = {
  neighboringTiles?: ITile[];
  center: [number, number];
  isCut: boolean;
  boundingBox: [number, number][];
  isEdgeTile: boolean;
};

export type ArrWithCoordinates = [[number, number], [number, number]][];

export type ICeilingGrid = {
  V: number[];
  W: number[];
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
  transformedRoom: number[][];
  gridLines?: ArrWithCoordinates;
  tiles?: ITile[][];
  id?: IObjectId;
};

type DeviceObject = {
  boundingBox: number[][];
  direction?: number[];
};

type PolygonBox = {
  origin: number[];
  localMax: number[];
  xAxis: number[];
  yAxis: number[];
};

const xSize = 30;
const ySize = 30;
const tileSize = 0.6;
const getLength = (segment: number[][]): number => {
  const xDistance = segment[1][0] - segment[0][0];
  const yDistance = segment[1][1] - segment[0][1];
  return Math.sqrt(xDistance * xDistance + yDistance * yDistance);
};

export const scalarProd = (v: number[], w: number[]): number => {
  return v[0] * w[0] + v[1] * w[1];
};

export const vectorAdd = (v: number[], w: number[]): [number, number] => {
  return [v[0] + w[0], v[1] + w[1]];
};

export const vectorSubtract = (v: number[], w: number[]): [number, number] => {
  return [v[0] - w[0], v[1] - w[1]];
};

export const project = (point: number[], V: number[], W: number[]): [number, number] => {
  return [scalarProd(point, V), scalarProd(point, W)];
};

export const transform = (point: number[], V: number[], W: number[]): [number, number] => {
  return [V[0] * point[0] + W[0] * point[1], V[1] * point[0] + W[1] * point[1]];
};

export const getRoomPic = (room: number[][], vector?: number[], point?: number[]): ICeilingGrid => {
  let segment: number[][] = [];
  let segmentLength = 0;

  if (vector) {
    segment = [[0, 0], vector];
    segmentLength = getLength(segment);
  } else {
    for (let i = 0; i < room.length - 1; i++) {
      const newLength = getLength([room[i], room[i + 1]]);
      if (newLength > segmentLength) {
        segmentLength = newLength;
        segment = [room[i], room[i + 1]];
      }
    }
  }

  const wx = (segment[1][0] - segment[0][0]) / segmentLength;
  const wy = (segment[1][1] - segment[0][1]) / segmentLength;
  const V = [wy, -wx];
  const W = [wx, wy];
  const transformedRoom = room.map((point) => project(point, V, W));

  let xMin = transformedRoom[0][0];
  let xMax = transformedRoom[0][0];
  let yMin = transformedRoom[0][1];
  let yMax = transformedRoom[0][1];

  for (const point of transformedRoom) {
    if (point[0] < xMin) xMin = point[0];
    if (point[0] > xMax) xMax = point[0];
    if (point[1] < yMin) yMin = point[1];
    if (point[1] > yMax) yMax = point[1];
  }

  const xLim = xMin + xSize * tileSize;
  const yLim = yMin + ySize * tileSize;

  if (xLim < xMax || yLim < yMax) {
    // throw new RangeError("Room polygon does not fit inside network input grid");
  }

  if (point) {
    const pointLocal = project(point, V, W);
    const dx = (pointLocal[0] - xMin) % tileSize;
    const dy = (pointLocal[1] - yMin) % tileSize;
    xMin -= tileSize - dx;
    yMin -= tileSize - dy;
  }

  return { V, W, xMin, yMin, xMax, yMax, transformedRoom };
};

const getBoundingBox = (center: number[], grid: ICeilingGrid): number[][] => {
  return [
    [
      center[0] + ((-grid.V[0] - grid.W[0]) * tileSize) / 2,
      center[1] + ((-grid.V[1] - grid.W[1]) * tileSize) / 2,
    ],
    [
      center[0] + ((-grid.V[0] + grid.W[0]) * tileSize) / 2,
      center[1] + ((-grid.V[1] + grid.W[1]) * tileSize) / 2,
    ],
    [
      center[0] + ((grid.V[0] + grid.W[0]) * tileSize) / 2,
      center[1] + ((grid.V[1] + grid.W[1]) * tileSize) / 2,
    ],
    [
      center[0] + ((grid.V[0] - grid.W[0]) * tileSize) / 2,
      center[1] + ((grid.V[1] - grid.W[1]) * tileSize) / 2,
    ],
    [
      center[0] + ((-grid.V[0] - grid.W[0]) * tileSize) / 2,
      center[1] + ((-grid.V[1] - grid.W[1]) * tileSize) / 2,
    ],
  ];
};

const getPartialBoundingBox = (
  localLimits: [[number, number], [number, number]],
  grid: ICeilingGrid
): [number, number][] => {
  const xMin = localLimits[0][0];
  const yMin = localLimits[1][0];
  const xMax = localLimits[0][1];
  const yMax = localLimits[1][1];
  const points = [
    [xMin, yMin],
    [xMin, yMax],
    [xMax, yMax],
    [xMax, yMin],
    [xMin, yMin],
  ];
  return points.map((point) => transform(point, grid.V, grid.W));
};

const centerOfBoundingBox = (boundingBox: number[][]): number[] => {
  if (boundingBox.length !== 5) {
    throw new RangeError(`BoundingBox not supported for device of type`);
  }
  const xMean =
    boundingBox.slice(0, -1).reduce((sum, point) => sum + point[0], 0) / (boundingBox.length - 1);
  const yMean =
    boundingBox.slice(0, -1).reduce((sum, point) => sum + point[1], 0) / (boundingBox.length - 1);
  return [xMean, yMean];
};

const alignBoundingBoxToGrid = (obj: DeviceObject, grid: ICeilingGrid): DeviceObject => {
  const boundingBoxCenter = centerOfBoundingBox(obj.boundingBox);
  const tile = getGridTile(boundingBoxCenter as [number, number], grid);
  const localCenter = project(boundingBoxCenter, grid.V, grid.W);
  const tilePos = [
    Math.floor((localCenter[0] - grid.xMin) / tileSize),
    Math.floor((localCenter[1] - grid.yMin) / tileSize),
  ];
  const newLocalCenter = [
    grid.xMin + (tilePos[0] + 0.5) * tileSize,
    grid.yMin + (tilePos[1] + 0.5) * tileSize,
  ];
  const newCenter = transform(newLocalCenter, grid.V, grid.W);
  obj.boundingBox = getBoundingBox(tile ? tile.center : newCenter, grid);
  return obj;
};

const alignVectorToGrid = (direction: number[], grid: ICeilingGrid): number[] => {
  const scalars = [scalarProd(grid.V, direction), scalarProd(grid.W, direction)];
  let newDirection = grid.V;
  if (Math.abs(scalars[1]) > Math.abs(scalars[0])) {
    newDirection = grid.W;
  }
  if (scalarProd(newDirection, direction) < 0) {
    newDirection = [-newDirection[0], -newDirection[1]];
  }
  return newDirection;
};

export const adjustObject = (obj: DeviceObject, grid: ICeilingGrid): DeviceObject => {
  const newObj = { ...obj };
  const nearestTile = getGridTile(centerOfBoundingBox(newObj.boundingBox) as [number, number], grid);
  if (nearestTile === undefined) {
    newObj.boundingBox = alignBoundingBoxToGrid(newObj, grid).boundingBox;
  } else {
    newObj.boundingBox = getBoundingBox(nearestTile.center, grid);
  }
  if (newObj.direction) {
    newObj.direction = alignVectorToGrid(newObj.direction, grid);
  }
  return newObj;
};

const getGridTile = (point: [number, number], grid: ICeilingGrid): ITile | undefined => {
  const localPoint = project(point, grid.V, grid.W);
  const index = [
    Math.floor((localPoint[0] - grid.xMin) / tileSize),
    Math.floor((localPoint[1] - grid.yMin) / tileSize),
  ];
  if (!grid.tiles) return undefined;
  if (index[0] < 0 || index[0] >= grid.tiles.length) return undefined;
  if (index[1] < 0 || index[1] >= grid.tiles[index[0]].length) return undefined;
  return grid.tiles[index[0]][index[1]];
};

export const calculateRoomGrid = (grid: ICeilingGrid): ICeilingGrid => {
  // This function modifies grid by adding the optional tiles data
  let gridLines: [[number, number], [number, number]][] = [];
  const nTilesX = Math.ceil((grid.xMax - grid.xMin) / tileSize);
  const nTilesY = Math.ceil((grid.yMax - grid.yMin) / tileSize);
  let tiles = [];
  for (let i = 0; i < nTilesX; i++) {
    let tileRow = [];
    for (let j = 0; j < nTilesY; j++) {
      tileRow.push({
        boundingBox: [
          [Infinity, -Infinity],
          [Infinity, -Infinity],
        ] as [number, number][],
        isCut: false,
        isEdgeTile: false,
        center: [0, 0] as [number, number],
      });
    }
    tiles.push(tileRow);
  }
  const lines = grid.transformedRoom
    .slice(0, -1)
    .map((point, i) => [point, grid.transformedRoom[i + 1]]);

  let xIndexTile = 1;
  for (let x = grid.xMin + tileSize; x < grid.xMax; x += tileSize) {
    const intersectingLines = lines.filter(
      (line) =>
        Math.sign(line[0][0] - x) !== Math.sign(line[1][0] - x) && Math.sign(line[1][0] - x) !== 0
    );
    const yIntersections = intersectingLines
      .map(
        (line) =>
          ((x - line[0][0]) * line[1][1] + (line[1][0] - x) * line[0][1]) /
          (line[1][0] - line[0][0])
      )
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a - b);
    for (let i = 0; i < yIntersections.length; i += 2) {
      gridLines.push([
        [x, yIntersections[i]],
        [x, yIntersections[i + 1]],
      ]);

      let firstTile = Math.floor((yIntersections[i] - grid.yMin) / tileSize);
      if (yIntersections[i] > grid.yMin + firstTile * tileSize) {
        tiles[xIndexTile][firstTile].isCut = true;
        tiles[xIndexTile - 1][firstTile].isCut = true;
      }
      tiles[xIndexTile][firstTile].boundingBox[1][0] = Math.min(
        yIntersections[i],
        tiles[xIndexTile][firstTile].boundingBox[1][0]
      );
      tiles[xIndexTile - 1][firstTile].boundingBox[1][0] = Math.min(
        yIntersections[i],
        tiles[xIndexTile - 1][firstTile].boundingBox[1][0]
      );

      for (
        let yIndexTile = Math.ceil((yIntersections[i] - grid.yMin) / tileSize);
        yIndexTile < Math.floor((yIntersections[i + 1] - grid.yMin) / tileSize);
        yIndexTile += 1
      ) {
        // Whole tiles
        tiles[xIndexTile][yIndexTile].boundingBox[1] = [
          grid.yMin + yIndexTile * tileSize,
          grid.yMin + (yIndexTile + 1) * tileSize,
        ];
        tiles[xIndexTile - 1][yIndexTile].boundingBox[1] = [
          grid.yMin + yIndexTile * tileSize,
          grid.yMin + (yIndexTile + 1) * tileSize,
        ];
      }

      let lastTile = Math.floor((yIntersections[i + 1] - grid.yMin) / tileSize);
      if (yIntersections[i + 1] < grid.yMin + (lastTile + 1) * tileSize) {
        tiles[xIndexTile][lastTile].isCut = true;
        tiles[xIndexTile - 1][lastTile].isCut = true;
      }
      tiles[xIndexTile][lastTile].boundingBox[1][1] = Math.max(
        yIntersections[i + 1],
        tiles[xIndexTile][lastTile].boundingBox[1][1]
      );
      tiles[xIndexTile - 1][lastTile].boundingBox[1][1] = Math.max(
        yIntersections[i + 1],
        tiles[xIndexTile - 1][lastTile].boundingBox[1][1]
      );

      if (firstTile !== lastTile) {
        tiles[xIndexTile][firstTile].boundingBox[1][1] = grid.yMin + (firstTile + 1) * tileSize;
        tiles[xIndexTile - 1][firstTile].boundingBox[1][1] = grid.yMin + (firstTile + 1) * tileSize;
        tiles[xIndexTile][lastTile].boundingBox[1][0] = grid.yMin + lastTile * tileSize;
        tiles[xIndexTile - 1][lastTile].boundingBox[1][0] = grid.yMin + lastTile * tileSize;
      }
    }
    xIndexTile += 1;
  }

  let yIndexTile = 1;
  for (let y = grid.yMin + tileSize; y < grid.yMax; y += tileSize) {
    const intersectingLines = lines.filter(
      (line) =>
        Math.sign(line[0][1] - y) !== Math.sign(line[1][1] - y) && Math.sign(line[0][1] - y) !== 0
    );
    const xIntersections = intersectingLines
      .map(
        (line) =>
          ((y - line[0][1]) * line[1][0] + (line[1][1] - y) * line[0][0]) /
          (line[1][1] - line[0][1])
      )
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a - b);
    for (let i = 0; i < xIntersections.length; i += 2) {
      gridLines.push([
        [xIntersections[i], y],
        [xIntersections[i + 1], y],
      ]);

      let firstTile = Math.floor((xIntersections[i] - grid.xMin) / tileSize);
      if (xIntersections[i] > grid.xMin + firstTile * tileSize) {
        tiles[firstTile][yIndexTile].isCut = true;
        tiles[firstTile][yIndexTile - 1].isCut = true;
      }
      tiles[firstTile][yIndexTile].boundingBox[0][0] = Math.min(
        xIntersections[i],
        tiles[firstTile][yIndexTile].boundingBox[0][0]
      );
      tiles[firstTile][yIndexTile - 1].boundingBox[0][0] = Math.min(
        xIntersections[i],
        tiles[firstTile][yIndexTile - 1].boundingBox[0][0]
      );

      for (
        let xIndexTile = Math.ceil((xIntersections[i] - grid.xMin) / tileSize);
        xIndexTile < Math.floor((xIntersections[i + 1] - grid.xMin) / tileSize);
        xIndexTile += 1
      ) {
        // Whole tiles
        tiles[xIndexTile][yIndexTile].boundingBox[0] = [
          grid.xMin + xIndexTile * tileSize,
          grid.xMin + (xIndexTile + 1) * tileSize,
        ];
        tiles[xIndexTile][yIndexTile - 1].boundingBox[0] = [
          grid.xMin + xIndexTile * tileSize,
          grid.xMin + (xIndexTile + 1) * tileSize,
        ];
      }

      let lastTile = Math.floor((xIntersections[i + 1] - grid.xMin) / tileSize);
      if (xIntersections[i + 1] < grid.xMin + (lastTile + 1) * tileSize) {
        tiles[lastTile][yIndexTile].isCut = true;
        tiles[lastTile][yIndexTile - 1].isCut = true;
      }

      tiles[lastTile][yIndexTile].boundingBox[0][1] = Math.max(
        xIntersections[i + 1],
        tiles[lastTile][yIndexTile].boundingBox[0][1]
      );
      tiles[lastTile][yIndexTile - 1].boundingBox[0][1] = Math.max(
        xIntersections[i + 1],
        tiles[lastTile][yIndexTile - 1].boundingBox[0][1]
      );

      if (firstTile !== lastTile) {
        tiles[firstTile][yIndexTile].boundingBox[0][1] = grid.xMin + (firstTile + 1) * tileSize;
        tiles[firstTile][yIndexTile - 1].boundingBox[0][1] = grid.xMin + (firstTile + 1) * tileSize;
        tiles[lastTile][yIndexTile].boundingBox[0][0] = grid.xMin + lastTile * tileSize;
        tiles[lastTile][yIndexTile - 1].boundingBox[0][0] = grid.xMin + lastTile * tileSize;
      }
    }
    yIndexTile += 1;
  }

  // Connect neighboring tiles, check if tile is on edge, calculate bounding box? //getBoundingBox()
  tiles.forEach((tileRow) => {
    tileRow.forEach((tile) => {
      if (tile.boundingBox[0][0] !== Infinity) {
        let localCoordCenter = [
          (tile.boundingBox[0][0] + tile.boundingBox[0][1]) / 2,
          (tile.boundingBox[1][0] + tile.boundingBox[1][1]) / 2,
        ];
        tile.center = transform(localCoordCenter, grid.V, grid.W);
        tile.boundingBox = getPartialBoundingBox(
          tile.boundingBox as [[number, number], [number, number]],
          grid
        );
        // Check if tile is on edge
        // Connect to neighboring tiles for more filtering rules
      }
    });
  });
  gridLines = gridLines.map((line) => [
    transform(line[0], grid.V, grid.W),
    transform(line[1], grid.V, grid.W),
  ]);
  grid.tiles = tiles;
  return {
    ...grid,
    gridLines,
    tiles,
  };
};

const returnLongest = (vector1: number[], vector2: number[]): number[] => {
  if (scalarProd(vector1, vector1) >= scalarProd(vector2, vector2)) {
    return vector1;
  }
  return vector2;
};

export const getLongestVector = (polygon: number[][]): number[] => {
  const allSides = polygon.map((vert, i, pol) => [
    pol[(i + 1) % pol.length][0] - vert[0],
    pol[(i + 1) % pol.length][1] - vert[1],
  ]);
  const longest = allSides.reduce(returnLongest, [0, 0]);
  const max = scalarProd(longest, longest);
  return longest.map((vert) => vert / Math.sqrt(max));
};

export const transformCoordinateUsingSystems = (
  coordinate: [number, number],
  source: PolygonBox,
  destination: PolygonBox
): [number, number] => {
  const localSource = project(
    vectorSubtract(coordinate, source.origin),
    source.xAxis,
    source.yAxis
  );
  return vectorAdd(
    transform(localSource, destination.xAxis, destination.yAxis),
    destination.origin
  );
};

export const transformDirectionUsingSystems = (
  direction: [number, number],
  source: PolygonBox,
  destination: PolygonBox
): [number, number] => {
  const localDirection = project(direction, source.xAxis, source.yAxis);
  const globalDirection = transform(localDirection, destination.xAxis, destination.yAxis);
  return globalDirection;
};

export const getPolygonCoordinateSystem = (polygon: number[][]): PolygonBox => {
  const xAxis = getLongestVector(polygon);
  const yAxis = [-xAxis[1], xAxis[0]];
  const projX = polygon.map((vert) => scalarProd(vert, xAxis));
  const projY = polygon.map((vert) => scalarProd(vert, yAxis));
  const originLocal = [Math.min(...projX), Math.min(...projY)];
  const furthest = [Math.max(...projX), Math.max(...projY)];
  const origin = transform(originLocal, xAxis, yAxis);
  let polyBox: PolygonBox = {
    xAxis: xAxis,
    yAxis: yAxis,
    origin: origin,
    localMax: vectorSubtract(furthest, originLocal),
  };
  return polyBox;
};

export const getLocalPolygon = (polygon: number[][], polyBox: PolygonBox): number[][] => {
  const localPolygon = polygon
    .map((vert) => vectorSubtract(vert, polyBox.origin))
    .map((vert) => project(vert, polyBox.xAxis, polyBox.yAxis));
  return localPolygon;
};

export const getGlobalPolygon = (
  localPolygon: [number, number][],
  polyBox: PolygonBox
): [number, number][] => {
  const polygon = localPolygon
    .map((vert) => transform(vert, polyBox.xAxis, polyBox.yAxis))
    .map((vert) => vectorAdd(vert, polyBox.origin));
  return polygon;
};

export const mirrorPolygonBox = (polyBox: PolygonBox): PolygonBox => {
  const mirroredPolyBox: PolygonBox = {
    xAxis: polyBox.yAxis,
    yAxis: polyBox.xAxis,
    origin: polyBox.origin,
    localMax: [polyBox.localMax[1], polyBox.localMax[0]],
  };
  return mirroredPolyBox;
};
export const rotatePolygonBox90Deg = (polyBox: PolygonBox): PolygonBox => {
  const rotatedPolyBox: PolygonBox = {
    xAxis: polyBox.yAxis,
    yAxis: [-polyBox.xAxis[0], -polyBox.xAxis[1]],
    origin: vectorAdd(polyBox.origin, transform([polyBox.localMax[0], 0], polyBox.xAxis, [0, 0])),
    localMax: [polyBox.localMax[1], polyBox.localMax[0]],
  };
  return rotatedPolyBox;
};

export const overlapAlong = (
  origin: number,
  dim: number,
  polygon0: number[][],
  polygon1: number[][]
): number[] => {
  const allSides0 = polygon0.map((vert, i, pol) => [vert, pol[(i + 1) % pol.length]]);
  const allSides1 = polygon1.map((vert, i, pol) => [vert, pol[(i + 1) % pol.length]]);
  allSides0
    .filter((side) => side[0][1 - dim] - origin !== side[1][1 - dim] - origin)
    .filter((side) => (side[0][1 - dim] - origin) * (side[1][1 - dim] - origin) <= 0);
  const intersect0 = allSides0
    .filter((side) => side[0][1 - dim] - origin !== side[1][1 - dim] - origin)
    .filter((side) => (side[0][1 - dim] - origin) * (side[1][1 - dim] - origin) <= 0)
    .map(
      (side) =>
        side[0][dim] +
        ((origin - side[0][1 - dim]) * (side[1][dim] - side[0][dim])) /
          (side[1][1 - dim] - side[0][1 - dim])
    )
    .map((side) => [0, side]);
  const intersect1 = allSides1
    .filter((side) => side[0][1 - dim] - origin !== side[1][1 - dim] - origin)
    .filter((side) => (side[0][1 - dim] - origin) * (side[1][1 - dim] - origin) <= 0)
    .map(
      (side) =>
        side[0][dim] +
        ((origin - side[0][1 - dim]) * (side[1][dim] - side[0][dim])) /
          (side[1][1 - dim] - side[0][1 - dim])
    )
    .map((side) => [1, side]);
  const allIntersects = [...intersect0, ...intersect1];
  const intersects = allIntersects.sort((a, b) => a[1] - b[1]);
  let nonSharedLength = 0;
  let sharedLength = 0;
  let inside = [false, false];
  let pos = 0;
  for (let intersect of intersects) {
    if (inside[0] && inside[1]) {
      sharedLength += intersect[1] - pos;
    } else if (inside[0] || inside[1]) {
      nonSharedLength += intersect[1] - pos;
    }
    inside[intersect[0]] = !inside[intersect[0]];
    pos = intersect[1];
  }
  return [sharedLength, nonSharedLength + sharedLength];
};
export const evaluateOverlap = (
  polyBox: PolygonBox,
  polygon1: number[][],
  polygon2: number[][]
): number => {
  let sharedArea = 0;
  let totalArea = 0;
  const stepDistance = polyBox.localMax[0] / 100;
  for (let x = 0; x < polyBox.localMax[0]; x += stepDistance) {
    const overlap = overlapAlong(x, 1, polygon1, polygon2);
    sharedArea += overlap[0] * stepDistance;
    totalArea += overlap[1] * stepDistance;
  }
  return sharedArea / totalArea;
};

interface IArgCopyDevicesFromASpaceToAnotherSpace {
  devicesToCopy: IDevice[];
  spaceToCopyFrom: PartialMcBimSpace;
  spaceToCopyTo: PartialMcBimSpace;
  roomToCopyTo: IRoomSummary | undefined;
  // ceilingCoordinateInSpaceToCopyTo?: number[];
}

export const copyDevicesFromASpaceToAnotherSpace = (
  args: IArgCopyDevicesFromASpaceToAnotherSpace
): IDevice[] => {
  if (args.spaceToCopyFrom.polygon == null || args.spaceToCopyTo.polygon == null) {
    return [];
  }

  let sourceBox = getPolygonCoordinateSystem(args.spaceToCopyFrom.polygon);
  let destinationBox = getPolygonCoordinateSystem(args.spaceToCopyTo.polygon);

  let bestOverlap = 0;
  let bestSource = sourceBox;
  let bestDestination = destinationBox;

  //  console.log("Start looking for overlap");
  for (let mirror of [0, 1]) {
    for (let rot = 0; rot < 4; rot += 1) {
      let destinationPoly = getLocalPolygon(args.spaceToCopyTo.polygon, destinationBox);
      for (let ro = 0; ro < 4; ro += 1) {
        let sourcePoly = getLocalPolygon(args.spaceToCopyFrom.polygon, sourceBox);
        let sharedBox: PolygonBox = JSON.parse(JSON.stringify(destinationBox));
        sharedBox.localMax = sharedBox.localMax.filter((val, index) =>
          Math.max(val, sourceBox.localMax[index])
        );
        let overlap = evaluateOverlap(sharedBox, sourcePoly, destinationPoly); // Take best overlap
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestSource = sourceBox;
          bestDestination = destinationBox;
        }
        sourceBox = rotatePolygonBox90Deg(sourceBox);
      }
      destinationBox = rotatePolygonBox90Deg(destinationBox);
    }
    sourceBox = mirrorPolygonBox(sourceBox);
  }
  // 0. < bestOverlap < 1. indicates how well the rooms match.
  const movedDevices = args.devicesToCopy.reduce((acc, device) => {
    if (device.coordinate == null) {
      return acc;
    }
    const copyOfDevice = { ...device };
    const newCoordinate = transformCoordinateUsingSystems(
      device.coordinate as [number, number],
      bestSource,
      bestDestination
    );
    const newDirection = transformDirectionUsingSystems(
      copyOfDevice.direction as [number, number],
      bestSource,
      bestDestination
    );
    if (args.roomToCopyTo?.ceilingCoordinate != null) {
      const roomPic = getRoomPic(
        args.spaceToCopyTo.polygon as number[][],
        undefined,
        args.roomToCopyTo.ceilingCoordinate
      );
      const boundingBox = getSqaureAroundCoordinate(
        newCoordinate as [number, number],
        newDirection as [number, number]
      );
      const gridWithTiles = calculateRoomGrid(roomPic);
      const updatedPositionAfterCeilingAdjustments = adjustObject(
        {
          boundingBox: boundingBox,
          direction: newDirection,
        },
        gridWithTiles
      );
      copyOfDevice.direction =
        updatedPositionAfterCeilingAdjustments.direction as IDirectionDepricated;
      copyOfDevice.coordinate = centerOfBoundingBox(
        updatedPositionAfterCeilingAdjustments.boundingBox
      ) as [number, number];
    }

    delete copyOfDevice._id;
    copyOfDevice._id = generateRandomString(5); // Temporary id before saving to db, required for viewer

    let newDevice: IDevice = {
      ...copyOfDevice,
      roomNumber: args.spaceToCopyTo.name,
    };
    if (newDevice.deviceCategory === "SUPPLY_AIR_DEVICE") {
      newDevice.supplyAirFlow = args.roomToCopyTo?.supplyAirFlow ?? 0;
    }
    if (newDevice.deviceCategory === "RETURN_AIR_DEVICE") {
      newDevice.returnAirFlow = args.roomToCopyTo?.returnAirFlow ?? 0;
    }
    acc.push(newDevice);
    return acc;
  }, [] as IDevice[]);
  //  console.log(movedDevices);
  return movedDevices;
};
